import SkeletonRow from '@/components/UI/molecules/tables/SkeletonRow'; // Asegúrate de proporcionar la ruta correcta
import {
    TableCell,
    TableCellHead,
    TableCellHeadInitial,
    TableContainerBinnance,
    TableHeadRow,
} from '@/components/UI/molecules/tables/TablePrincipal';
import {FeatureCodeEnum, GuestEnum} from '@/enums/permissions.enum';
import StatusTextGenecic from '@/components/UI/atoms/buttons/StatusTextGeneric';
import CustomPagination from '@components/UI/organisms/pagination/Pagination';
import {fetchGetExpenseAccounts} from '@components/orderSimply/redux/actions/OrdersSimplyActions';
import {AppDispatch, RootState} from '@config/store';
import {
    Box,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {ACLObj} from '@components/auth/guard/AclGuard';
import {DateTime} from 'luxon';

import {
    detailExpendAccount,
    expenseAccountSummary,
    expenseAccountVersion,
} from '@components/orderSimply/redux/types/OrderAgentsTypes';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';

/**
 * Anchos de las 16 columnas. Suman 12, que es la rejilla de MUI. Se declaran aquí y no
 * repartidos por el JSX para que agregar o quitar una columna no descuadre la tabla.
 */
const COLS = {
    cveImport: 0.5,
    description: 1,
    subtotal: 0.8,
    idIva: 0.5,
    iva: 0.5,
    ivaAmount: 0.8,
    retentionFactor: 0.6,
    retention: 0.8,
    total: 0.9,
    numSupplier: 0.7,
    nameSupplier: 1.2,
    numInvoice: 0.8,
    dateInvoice: 0.7,
    status: 0.9,
    paymentDate: 0.7,
    message: 0.6,
};

const CELL = {fontSize: '9.5px'};

const formatDate = (value?: string | null) =>
    value ? DateTime.fromISO(value).toFormat('dd/MM/yyyy') : '---';

interface IProps {
    /**
     * Módulo desde el que se abrió. `internal` es Validación de Cuenta de Gastos: cambia el
     * endpoint —el suyo va bajo su propio permiso— y a dónde vuelve la flecha.
     */
    origin?: 'internal';
}

const DetailExpendAccount: React.FC<IProps> & {acl?: ACLObj} = ({origin}) => {
    const isInternal = origin === 'internal';
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {customsAgentCode, customRequest} = useParams();
    const theme = useTheme();
    const dispatch: AppDispatch = useDispatch();
    const token = useSelector((state: RootState) => state.logins.accessToken);

    const expenseAccountList = useSelector(
        (state: RootState) => state.orderssimply.expenseAccountList
    );
    const currentPage = useSelector((state: RootState) => state.orderssimply.pageExpenseAccount);
    const limit = useSelector((state: RootState) => state.orderssimply.limitExpenseAccount);

    const totalPages = useSelector(
        (state: RootState) => state.orderssimply.totalPagesExpenseAccount
    );
    const loading = useSelector((state: RootState) => state.orderssimply.loadingExpenseAccount);
    const expenseAccount: expenseAccountSummary | undefined = useSelector(
        (state: RootState) => state.orderssimply.expenseAccountSummary
    );
    const versions: expenseAccountVersion[] = useSelector(
        (state: RootState) => state.orderssimply.expenseAccountVersions
    );

    const shownVersion = versions?.find((item) => item.current)?.version ?? '';

    const load = (page: number, rows: number, version?: number) => {
        dispatch(
            fetchGetExpenseAccounts(
                customsAgentCode,
                customRequest,
                token,
                page,
                rows,
                version,
                // El módulo interno consulta su propio endpoint, bajo su permiso.
                isInternal
            )
        );
    };

    /**
     * Esta pantalla la abren dos módulos distintos. Sin esto, la flecha regresaba SIEMPRE a
     * Agentes Aduanales y sacaba al usuario interno de su módulo, que además puede no tener
     * permiso de ver. Sobrevive a una recarga porque depende de la ruta, no del historial.
     */
    const handleGoBack = () => {
        navigate(isInternal ? '/expense-accounts-validation' : '/poagents');
    };
    const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
        // Antes recargaba el LISTADO de pedimentos en vez del detalle, así que cambiar de
        // página no traía las facturas siguientes.
        load(value, limit, shownVersion || undefined);
    };
    const handleChangeRowsPerPage = (event: any) => {
        load(1, event.target.value, shownVersion || undefined);
    };
    const handleChangeVersion = (event: any) => {
        load(1, limit, Number(event.target.value));
    };

    useEffect(() => {
        if (token && customsAgentCode) {
            load(currentPage, limit);
        }
    }, [dispatch, token]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                padding: '15px 23px',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 0',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '24px',
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: 400,
                    }}
                >
                    <IconButton onClick={handleGoBack} aria-label="Go back">
                        <ArrowBackIcon />
                    </IconButton>
                    {t('logbook_and_details.details')}:
                    {expenseAccount?.folio ? ` ${expenseAccount.folio}` : ''}
                </Typography>

                {versions?.length > 1 && (
                    <TextField
                        select
                        size="small"
                        label={t('poagents.detail.version')}
                        value={shownVersion}
                        onChange={handleChangeVersion}
                        sx={{minWidth: '190px'}}
                    >
                        {versions.map((item) => (
                            <MenuItem key={item.version} value={item.version}>
                                {`v${item.version} · ${t(`poagents.status.${item.status}`)}`}
                                {item.current ? ` (${t('poagents.detail.version_current')})` : ''}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            </Box>

            {expenseAccount?.rejectionReason && (
                <Box
                    sx={{
                        border: '1px solid #E57373',
                        borderLeft: '4px solid #E57373',
                        borderRadius: '4px',
                        padding: '10px 14px',
                        marginBottom: '10px',
                    }}
                >
                    <Typography sx={{fontSize: '12px', fontWeight: 700}}>
                        {t('poagents.detail.rejected_title')}
                    </Typography>
                    <Typography sx={{fontSize: '11px', marginTop: '4px'}}>
                        {`${t('poagents.detail.rejected_reason')}: ${
                            expenseAccount.rejectionReason
                        }`}
                    </Typography>
                    {expenseAccount.rejectedBy && (
                        <Typography sx={{fontSize: '10px', color: '#6E7076'}}>
                            {`${t('poagents.detail.rejected_by')}: ${
                                expenseAccount.rejectedBy
                            } · ${formatDate(expenseAccount.rejectedAt)}`}
                        </Typography>
                    )}
                    {/* El primer comentario es el motivo, que ya se muestra arriba. */}
                    {expenseAccount.comments?.length > 1 && (
                        <Box sx={{marginTop: '6px'}}>
                            <Typography sx={{fontSize: '10px', fontWeight: 700}}>
                                {t('poagents.detail.comments')}
                            </Typography>
                            {expenseAccount.comments.slice(1).map((comment) => (
                                <Typography
                                    key={`${comment.createdAt}-${comment.text}`}
                                    sx={{fontSize: '10px'}}
                                >
                                    {comment.text}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            <TableContainerBinnance container>
                <TableHeadRow container>
                    {/* Primera fila */}
                    <Grid item xs={COLS.cveImport}>
                        <TableCellHeadInitial sx={CELL}>
                            {t('poagents.table.headers.cve_import')}
                        </TableCellHeadInitial>
                    </Grid>
                    <Grid item xs={COLS.description}>
                        <TableCellHead sx={CELL}>
                            {t('poagents.table.headers.description')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.subtotal}>
                        <TableCellHead sx={CELL}>
                            {t('poagents.table.headers.mx_import')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.idIva}>
                        <TableCellHead sx={CELL}>
                            {t('poagents.table.headers.id_iva')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.iva}>
                        <TableCellHead sx={CELL}>{t('poagents.table.headers.iva')}</TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.ivaAmount}>
                        <TableCellHead sx={CELL}>
                            {t('poagents.table.headers.iva_amount')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.retentionFactor}>
                        <TableCellHead sx={CELL}>
                            {t('poagents.table.headers.factor_retention')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.retention}>
                        <TableCellHead sx={CELL}>
                            {t('poagents.table.headers.retention')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.total}>
                        <TableCellHead sx={CELL}>{t('poagents.table.headers.brute')}</TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.numSupplier}>
                        <TableCellHead sx={CELL}>
                            {t('poagents.table.headers.num_supplier')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.nameSupplier}>
                        <TableCellHead sx={CELL}>
                            {t('poagents.table.headers.name_supplier')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.numInvoice}>
                        <TableCellHead sx={CELL}>
                            {t('poagents.table.headers.num_invoice')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.dateInvoice}>
                        <TableCellHead sx={CELL}>
                            {t('poagents.table.headers.date_invoice')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.status}>
                        <TableCellHead sx={CELL}>
                            {t('poagents.table.headers.status')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.paymentDate}>
                        <TableCellHead sx={CELL}>
                            {t('poagents.table.headers.payment_date')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.message}>
                        <TableCellHead sx={CELL}>
                            {t('poagents.table.headers.message')}
                        </TableCellHead>
                    </Grid>
                </TableHeadRow>
                {loading ? (
                    <>
                        <SkeletonRow />
                        <SkeletonRow />
                        <SkeletonRow />
                    </>
                ) : (
                    (expenseAccountList ?? []).map((item: detailExpendAccount) => (
                        <React.Fragment key={`${item.invoiceNumber}-${item.supplierNumbers}`}>
                            <Grid item xs={COLS.cveImport} display="grid">
                                <TableCell sx={CELL}>
                                    {item.inconsistency ? (
                                        // La factura forma parte de la Cuenta de Gastos y se
                                        // muestra, pero no se enviará a SAP/VIM hasta que se
                                        // dé de alta la conversión de clave.
                                        <Tooltip title={t('poagents.detail.inconsistency_sap_key')}>
                                            <WarningAmberIcon
                                                sx={{fontSize: '14px', color: '#ED8B00'}}
                                            />
                                        </Tooltip>
                                    ) : (
                                        item.importKeys
                                    )}
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.description} display="grid">
                                <TableCell sx={CELL}>{item.descriptions}</TableCell>
                            </Grid>
                            <Grid item xs={COLS.subtotal} display="grid">
                                <TableCell sx={CELL}>{item.amounts}</TableCell>
                            </Grid>
                            <Grid item xs={COLS.idIva} display="grid">
                                <TableCell sx={CELL}>{item.taxIds}</TableCell>
                            </Grid>
                            <Grid item xs={COLS.iva} display="grid">
                                <TableCell sx={CELL}>{item.taxesIva}</TableCell>
                            </Grid>
                            <Grid item xs={COLS.ivaAmount} display="grid">
                                <TableCell sx={CELL}>{item.ivaAmounts}</TableCell>
                            </Grid>
                            <Grid item xs={COLS.retentionFactor} display="grid">
                                <TableCell sx={CELL}>{item.retentionFactors}</TableCell>
                            </Grid>
                            <Grid item xs={COLS.retention} display="grid">
                                <TableCell sx={CELL}>{item.retentions}</TableCell>
                            </Grid>
                            <Grid item xs={COLS.total} display="grid">
                                <TableCell sx={CELL}>{item.grossAmounts}</TableCell>
                            </Grid>
                            <Grid item xs={COLS.numSupplier} display="grid">
                                <TableCell sx={CELL}>{item.supplierNumbers}</TableCell>
                            </Grid>
                            <Grid item xs={COLS.nameSupplier} display="grid">
                                <TableCell sx={CELL}>{item.supplierName}</TableCell>
                            </Grid>
                            <Grid item xs={COLS.numInvoice} display="grid">
                                <TableCell sx={CELL}>{item.invoiceNumber}</TableCell>
                            </Grid>
                            <Grid item xs={COLS.dateInvoice} display="grid">
                                <TableCell sx={CELL}>{formatDate(item.invoiceDate)}</TableCell>
                            </Grid>
                            <Grid item xs={COLS.status} display="grid">
                                <TableCell sx={CELL}>
                                    {item.status ? (
                                        <StatusTextGenecic
                                            status={item.status}
                                            text={t(`poagents.status.${item.status}`)}
                                        />
                                    ) : (
                                        '---'
                                    )}
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.paymentDate} display="grid">
                                <TableCell sx={CELL}>{formatDate(item.paymentDate)}</TableCell>
                            </Grid>
                            <Grid item xs={COLS.message} display="grid">
                                <TableCell sx={CELL}>{item.messages}</TableCell>
                            </Grid>
                        </React.Fragment>
                    ))
                )}
                {!loading && (expenseAccountList ?? []).length === 0 && (
                    <Grid item xs={12}>
                        <TableCell sx={{...CELL, padding: '16px', textAlign: 'center'}}>
                            {t('poagents.detail.empty')}
                        </TableCell>
                    </Grid>
                )}
            </TableContainerBinnance>
            <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                handleChange={handleChange}
                limit={limit}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
        </Box>
    );
};
DetailExpendAccount.acl = {
    action: GuestEnum.SEE_LIST,
    subject: FeatureCodeEnum.CUSTOMS_AGENTS,
};
export default DetailExpendAccount;
